/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { Route } from './+types/_app.$contentId[.html]';
import { fetchContentAssetHtml, type ContentAsset } from '@/lib/api/content-asset.server';
import { NormalizedApiError } from '@/lib/api/normalized-api-error';
import { SeoMeta } from '@/components/seo-meta';
import { buildCanonicalUrl } from '@/utils/canonical-url';
import { getPublicOrigin } from '@/utils/schema-url';
import { getLogger } from '@/lib/logger.server';
import { Region } from '@/components/region';
import { fetchPageWithComponentData, type PageWithComponentData } from '@/lib/page-designer/page-loader.server';
import { fetchWishlistInitialState } from '@/lib/wishlist/fetch-initial-state.server';
import type { WishlistInitialState } from '@/lib/wishlist/state';
import { WishlistProvider } from '@/providers/wishlist';

export type ContentAssetPageData =
    | { kind: 'content'; asset: ContentAsset; canonicalUrl: string }
    | {
          kind: 'page-designer';
          page: PageWithComponentData;
          canonicalUrl: string;
          /** Deferred — Product Tiles (e.g. inside a Product Carousel) use wishlist hooks. */
          wishlistInitialState: Promise<WishlistInitialState>;
      };

/**
 * Serves single-segment `.html` URLs (`/customer-service.html`) for SFRA/SiteGenesis URL
 * parity. Content-first: `contentId` is looked up directly as a content-asset id (no
 * `getUrlMapping`/slug-resolution step — see `docs/README-CONTENT-ASSET-ROUTE.md` section 5 for
 * the "slug === asset id" assumption this relies on).
 *
 * If no content asset matches, falls back to rendering the corresponding Page Designer page
 * matching the same `contentId`.
 */
export async function loader(args: Route.LoaderArgs): Promise<ContentAssetPageData> {
    const { request, params, context } = args;
    const logger = getLogger(context);
    const { contentId } = params;

    console.warn('ContentAssetRoute loader triggered: resolving id', contentId);

    const url = new URL(request.url);
    const canonicalUrl = buildCanonicalUrl(getPublicOrigin(request), url.pathname, url.search);

    // 1) Content-first: a single-segment `.html` is a content URL by default.
    try {
        const asset = await fetchContentAssetHtml(context, contentId);
        if (asset) {
            console.warn('ContentAssetRoute: successfully matched standard content asset for id', contentId);
            return { kind: 'content', asset, canonicalUrl };
        }
    } catch (e) {
        console.warn('ContentAssetRoute: content asset fetch failed or returned empty for id', contentId, e);
        const status = e instanceof NormalizedApiError ? e.status : undefined;
        if (status && status >= 500) {
            // A real failure (500/network/auth) — surface it, don't mask it.
            throw new Response(e instanceof Error ? e.message : 'Internal Server Error', { status });
        }
    }

    // 2) Page Designer fallback: Look up Page Designer layout configurations
    try {
        const page = await fetchPageWithComponentData(args, { pageId: contentId });
        if (page) {
            console.warn('ContentAssetRoute: successfully matched Page Designer page layout for id', contentId);
            const wishlistInitialState = fetchWishlistInitialState(context);
            wishlistInitialState.catch((error: unknown) => {
                logger.warn('ContentAssetRoute: wishlist initial state failed', {
                    error: error instanceof Error ? error.message : String(error),
                });
            });
            return { kind: 'page-designer', page, canonicalUrl, wishlistInitialState };
        }
    } catch (e) {
        console.warn('ContentAssetRoute: Page Designer page lookup failed/missed for id', contentId, e);
        // Fall through to 404
    }

    // 3) Neither content nor Page Designer layout — a real 404.
    console.warn('ContentAssetRoute: No content asset or Page Designer page found for id', contentId);
    logger.warn('No content asset or Page Designer page for id, returning 404', { contentId });
    throw new Response('Not Found', { status: 404 });
}

export default function ContentAssetRoute({ loaderData }: { loaderData: ContentAssetPageData }) {
    if (loaderData.kind === 'page-designer') {
        const { page, canonicalUrl, wishlistInitialState } = loaderData;
        return (
            <WishlistProvider initialState={wishlistInitialState}>
                <SeoMeta
                    title={page.name}
                    description={page.description}
                    openGraph={{ type: 'article', url: canonicalUrl }}
                />
                <div className="section-container py-6">
                    <Region page={page} regionId="main" />
                </div>
            </WishlistProvider>
        );
    }

    const { asset, canonicalUrl } = loaderData;
    return (
        <>
            <SeoMeta
                title={asset.title}
                description={asset.description}
                openGraph={{ type: 'article', url: canonicalUrl }}
            />
            <div className="section-container gap-4 lg:gap-x-12 lg:gap-y-6 content-asset-container">
                {asset.html && asset.html.includes('<h1') ? null : (
                   <h1 className="content-asset-title text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                    {asset.title}
                    </h1>
                )}
                
                <div
                    className="content-asset-body"
                    // eslint-disable-next-line react/no-danger -- sanitized server-side in fetchContentAssetHtml (src/lib/api/content-asset.server.ts)
                    dangerouslySetInnerHTML={{ __html: asset.html }}
                />
            </div>
        </>
    );
}
